import React, { memo, useContext, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { ModalContext } from '../../context';
import { useClientUser } from '@/hooks';
import { Grid, IconButton, MenuItem, TextField } from '@material-ui/core';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import { Body, SecondaryButton } from '@/styles/blocks';
import { OnwardPriceInput, OnwardTextField } from '../../../InputFields';
import UploadFile from './UploadFile';
import { AIRLINE_OPTIONS } from '../../../../constants';
import AdditionalDocuments from './AdditionalDocuments';
import OrderDetailsPricing from './OrderDetailsPricing';
import { isFinite } from 'lodash';
import { TabRow, TabSection, TabTitle } from '@/components/ShipmentForm/blocks';
import { useOrgShippers } from '@/components/ShipmentForm/hooks';
import { missingShipperFields } from '@/components/ShipmentForm/utilities/requiredFields';

/**
 * Edit Order modal Details tab: PO, reference, and manufacturer fields, OMS shipping partner and
 * rates, references, file uploads, and attached documents.
 * Takes no props; reads the order and callbacks from `ModalContext`.
 */
const OrderDetailsTab = memo(() => {
    const { state: modalState, callbacks } = useContext(ModalContext);
    const { isDirty, order, hasError, options } = modalState;
    const { shipping_partners, user_id, isCarrierAdmin } = useClientUser();
    const orgShippers = useOrgShippers();
    const shippers = isCarrierAdmin && orgShippers.length > 0 ? orgShippers : shipping_partners;
    // A carrier admin's new order must name a shipper, so "None" is not offered for it.
    const requireShipper = isCarrierAdmin && !order.order_id;
    const missingShipper = missingShipperFields(order, { isCarrierAdmin, userId: user_id }).length > 0;
    const [collapsed, setCollapsed] = useState(false);

    const showRevenueOption = useMemo(() => {
        if (order) {
            return order.wh_events?.some((event) => event.action.includes('ADD_CD'));
        }
        return false;
    }, [order]);

    let content;
    if (order.freight_type === 'pallet') {
        content = (
            <>
                <TabRow>
                    <Grid item xs={4}>
                        <TextField
                            select
                            fullWidth
                            variant="outlined"
                            label="Airline"
                            value={order.airline}
                            onChange={(e) => callbacks.modifyOrder({ airline: e.target.value })}
                        >
                            {AIRLINE_OPTIONS.map((airline) => (
                                <MenuItem key={airline} value={airline}>
                                    {airline}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid css={css`flex: 1; flex-basis;`}></Grid>
                </TabRow>
            </>
        );
    }

    return (
        <>
            <TabTitle
                collapsed={collapsed}
                callbacks={{ setCollapsed }}
                css={css`
                    align-items: center;
                    justify-content: space-between;
                    ${collapsed ? '' : 'margin-bottom: 20px;'}
                `}
            >
                Details
            </TabTitle>
            {collapsed ? null : (
                <>
                    <TabRow>
                        <Grid item xs={3}>
                            <OnwardTextField
                                fullWidth
                                variant="outlined"
                                label="PO #"
                                value={order.po_number}
                                onBlur={() => callbacks.makeDirty(['po_number'])}
                                onChange={(e) => callbacks.modifyOrder({ po_number: e.target.value })}
                                error={hasError.po_number && (options.startDirty || isDirty.po_number)}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <OnwardTextField
                                fullWidth
                                variant="outlined"
                                label="Ref #"
                                value={order.reference_id}
                                onBlur={() => callbacks.makeDirty(['reference_id'])}
                                onChange={(e) => callbacks.modifyOrder({ reference_id: e.target.value })}
                                error={hasError.reference_id && (options.startDirty || isDirty.reference_id)}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <OnwardTextField
                                fullWidth
                                variant="outlined"
                                label="Manufacturer"
                                value={order.manufacturer}
                                onBlur={() => callbacks.makeDirty(['manufacturer'])}
                                onChange={(e) => callbacks.modifyOrder({ manufacturer: e.target.value })}
                                error={hasError.manufacturer && (options.startDirty || isDirty.manufacturer)}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <OnwardTextField
                                fullWidth
                                variant="outlined"
                                label="ASN"
                                value={order.warehouse_manifest_number}
                                onBlur={() => callbacks.makeDirty(['warehouse_manifest_number'])}
                                onChange={(e) => callbacks.modifyOrder({ warehouse_manifest_number: e.target.value })}
                            />
                        </Grid>
                    </TabRow>

                    {order.oms || requireShipper ? (
                        <TabRow>
                            <Grid item xs={12}>
                                <TextField
                                    select
                                    variant="outlined"
                                    fullWidth
                                    label={`Shipping Partner${requireShipper ? ' *' : ''}`}
                                    value={missingShipper ? '' : order.shipper_id || ''}
                                    onChange={(e) => {
                                        const shipper_id = e.target.value;
                                        callbacks.modifyOrder({
                                            shipper_id,
                                            carrier_id: shipper_id !== user_id ? user_id : null,
                                        });
                                    }}
                                    error={missingShipper}
                                >
                                    {requireShipper ? null : <MenuItem value={user_id}>None</MenuItem>}
                                    {shippers.map((shipper) => (
                                        <MenuItem key={shipper.shipper_id} value={shipper.shipper_id}>
                                            {shipper.shipper.business_name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </TabRow>
                    ) : null}

                    {order.oms ? (
                        <>
                            <TabRow>
                                <Grid item xs={showRevenueOption ? 3 : 5}>
                                    <OnwardPriceInput
                                        disabled
                                        fullWidth
                                        variant="outlined"
                                        label="Total Rate"
                                        value={order.order_revenue}
                                    />
                                </Grid>
                                <Grid item xs={showRevenueOption ? 3 : 5}>
                                    <OnwardPriceInput
                                        fullWidth
                                        variant="outlined"
                                        label="Base Rate Override"
                                        value={order.order_revenue_override}
                                        onChange={(_, rate) =>
                                            callbacks.modifyOrder({
                                                order_revenue_override: rate,
                                                order_revenue_adjustments: {
                                                    ...(order.order_revenue_adjustments || {}),
                                                    internal: order.order_revenue_adjustments?.internal || [],
                                                },
                                            })
                                        }
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <SecondaryButton
                                        fullWidth
                                        onClick={() =>
                                            callbacks.modifyOrder({
                                                order_revenue_override: null,
                                                order_revenue_adjustments: {
                                                    ...(order.order_revenue_adjustments || {}),
                                                    internal: null,
                                                },
                                            })
                                        }
                                    >
                                        Reset
                                    </SecondaryButton>
                                </Grid>
                                {showRevenueOption && (
                                    <Grid item xs={4}>
                                        <TextField
                                            variant="outlined"
                                            select
                                            label="Order revenue applied to"
                                            name="warehouse_id"
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                            value={order.show_revenue_on}
                                            onChange={(e) => callbacks.modifyOrder({ show_revenue_on: e.target.value })}
                                        >
                                            <MenuItem key={'DROPOFF'} value={'DROPOFF'}>
                                                Delivery
                                            </MenuItem>
                                            <MenuItem key={'PICKUP'} value={'PICKUP'}>
                                                Pickup
                                            </MenuItem>
                                        </TextField>
                                    </Grid>
                                )}
                            </TabRow>

                            {order?.order_number ? (
                                <OrderDetailsPricing perspective="internal" />
                            ) : (
                                <Body className="mb-2">You can adjust accessorials upon final review/submission.</Body>
                            )}
                        </>
                    ) : null}

                    {content}

                    <TabSection>References</TabSection>
                    {(order.references || []).map((ref, idx) => (
                        <TabRow key={ref.reference_id || idx}>
                            <Grid item xs={3}>
                                <OnwardTextField
                                    fullWidth
                                    variant="outlined"
                                    label="Key"
                                    value={ref.key || ''}
                                    onBlur={() => callbacks.makeDirty(['references'])}
                                    onChange={(e) => callbacks.editReference(idx, { key: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <OnwardTextField
                                    fullWidth
                                    variant="outlined"
                                    label="Value"
                                    value={ref.value || ''}
                                    onBlur={() => callbacks.makeDirty(['references'])}
                                    onChange={(e) => callbacks.editReference(idx, { value: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <OnwardTextField
                                    fullWidth
                                    variant="outlined"
                                    label="Label"
                                    value={ref.label || ''}
                                    onChange={(e) => callbacks.editReference(idx, { label: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={1}>
                                <IconButton onClick={() => callbacks.deleteReference(idx)}>
                                    <DeleteOutlinedIcon />
                                </IconButton>
                            </Grid>
                        </TabRow>
                    ))}
                    <TabRow>
                        <Grid item xs={4}>
                            <SecondaryButton fullWidth onClick={() => callbacks.addReference()}>
                                Add reference
                            </SecondaryButton>
                        </Grid>
                    </TabRow>

                    <TabRow>
                        <Grid item xs={4}>
                            <UploadFile order={order} type={'waybill'} />
                        </Grid>
                        <Grid item xs={4}>
                            <UploadFile order={order} type={'pickup_notification'} />
                        </Grid>
                        <Grid item xs={4}>
                            <UploadFile order={order} type={'bol'} />
                        </Grid>
                    </TabRow>

                    <AdditionalDocuments callbacks={callbacks} />
                </>
            )}
        </>
    );
});

export default OrderDetailsTab;
